import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { HourDayHeatmapCell } from '@/types/reports';

interface HeatmapChartProps {
  data: HourDayHeatmapCell[];
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/** Gradable leads by hour and weekday of creation, in the company timezone. */
export function HeatmapChart({ data }: HeatmapChartProps) {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lead Distribution by Hour & Day</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No heatmap data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 1);
  const cells = new Map<string, number>();
  for (const d of data) cells.set(`${d.day}-${d.hour}`, d.count);

  const getColor = (normalizedValue: number): string => {
    if (normalizedValue === 0) return 'bg-muted/30';
    if (normalizedValue < 0.25) return 'bg-primary/20';
    if (normalizedValue < 0.5) return 'bg-primary/40';
    if (normalizedValue < 0.75) return 'bg-primary/60';
    return 'bg-primary/80';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Lead Distribution by Hour & Day</CardTitle>
        <p className="text-xs text-muted-foreground">Gradable leads by creation time, company timezone.</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Header row with hours */}
            <div className="flex mb-1">
              <div className="w-12 shrink-0" />
              {HOURS.map(hour => (
                <div key={hour} className="flex-1 text-center text-xs text-muted-foreground">
                  {hour.toString().padStart(2, '0')}
                </div>
              ))}
            </div>

            {/* Data rows */}
            {DAY_NAMES.map((dayName, dayIndex) => (
              <div key={dayName} className="flex mb-1">
                <div className="w-12 shrink-0 text-xs text-muted-foreground flex items-center">
                  {dayName}
                </div>
                {HOURS.map(hour => {
                  const value = cells.get(`${dayIndex}-${hour}`) ?? 0;
                  return (
                    <div
                      key={hour}
                      className={`flex-1 aspect-square rounded-sm mx-0.5 ${getColor(value / maxCount)} flex items-center justify-center cursor-default`}
                      title={`${dayName} ${hour}:00 - ${value} lead${value === 1 ? '' : 's'}`}
                    >
                      {value > 0 && (
                        <span className="text-[10px] font-medium text-foreground/80">{value}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center justify-end gap-2 mt-4 text-xs text-muted-foreground">
              <span>Low</span>
              <div className="flex gap-1">
                <div className="w-4 h-4 rounded bg-muted/30" />
                <div className="w-4 h-4 rounded bg-primary/20" />
                <div className="w-4 h-4 rounded bg-primary/40" />
                <div className="w-4 h-4 rounded bg-primary/60" />
                <div className="w-4 h-4 rounded bg-primary/80" />
              </div>
              <span>High</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
